import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './TenantAttachmentItem.module.scss';
import {
  Button,
  Form,
  Icon,
  Input,
  message,
  Select,
  Tooltip,
  Upload,
} from 'antd';
import { documentsAllowedExtensions } from '../../../../../../constants/documentsAllowedExtensions';
import { maxFileSize } from '../../../../../../../../constants/maxFileSize';
import cn from 'classnames';

const beforeUpload = (file) => {
  const fileExtension = file.name.split('.').pop().toLowerCase();

  if (!documentsAllowedExtensions.includes(fileExtension)) {
    message.error(`Файл ${file.name} имеет недопустимый формат.`);
    return false;
  }

  if (file.size > maxFileSize) {
    message.error(`Файл ${file.name} весит больше 15 мб.`);
    return false;
  }

  return true;
};

export const TenantAttachmentItem = ({
  getFieldDecorator,
  setFieldsValue,
  usedSubTypes,
  subTypes = [],
  index,
  onRemoveTenantAttachmentField,
  fieldKey,
  setDocuments,
  setUsedSubTypes,
  addTenantAttachmentItem,
  isLastElem,
}) => {
  const [attach, setAttach] = useState(null);
  const [currentSubname, setCurrentSubname] = useState(undefined);
  const prevSubnameRef = useRef();
  const SUBNAME_KEY = `subname-${fieldKey}-${index}`;

  const handleAddAttachment = useCallback(
    ({ file, onSuccess }) => {
      if (!currentSubname) {
        message.error('Сначала выберите или введите наименование документа.');
        return;
      }

      const fileExtension = file.name.split('.').pop();
      const newFileName = `${currentSubname}.${fileExtension}`;
      const renamedFile = [
        {
          originFileObj: new File([file], newFileName, {
            type: file.type,
          }),
          uid: file.uid,
          fileType: 'file',
        },
      ];

      setDocuments((prev) => [...prev, ...renamedFile]);
      setAttach(renamedFile);
      onSuccess();
    },
    [currentSubname, setDocuments]
  );

  const handleChange = (value) => {
    setUsedSubTypes((prev) => {
      const updated = new Set(prev);
      if (prevSubnameRef.current) {
        updated.delete(prevSubnameRef.current);
      }
      updated.add(value);
      return updated;
    });

    setFieldsValue({ [SUBNAME_KEY]: value });
    setCurrentSubname(value);
    prevSubnameRef.current = value;
  };

  const handleSearch = (value) => {
    if (value && !subTypes.some((subType) => subType.name === value)) {
      setCurrentSubname(value);
      setFieldsValue({ [SUBNAME_KEY]: value });
    }
  };

  useEffect(() => {
    if (!currentSubname && !!attach?.length) {
      setDocuments((prev) => prev.filter((f) => f.uid !== attach[0].uid));
      setAttach(null);
    }
  }, [attach, currentSubname, setDocuments]);

  const handleRemoveAttachment = () => {
    if (!!attach?.length) {
      setDocuments((prev) => prev.filter((f) => f.uid !== attach[0].uid));
    }
    setAttach(null);
  };

  const handleRemoveField = (fieldKey) => {
    handleRemoveAttachment();

    if (prevSubnameRef.current) {
      setUsedSubTypes((prev) => {
        const next = new Set(prev);
        next.delete(prevSubnameRef.current);
        return next;
      });
    }

    onRemoveTenantAttachmentField(fieldKey);
  };

  const handleInputChange = (e) => {
    setCurrentSubname(e.target.value);
    setFieldsValue({ [SUBNAME_KEY]: e.target.value });
  };

  return (
    <div className={styles.container}>
      <div className={styles.root}>
        <div className={cn(styles.content, styles.content_left)}>
          {isLastElem && (
            <div className={styles.plusBtn}>
              <Button
                className={styles.plus}
                icon="plus"
                shape="circle"
                onClick={addTenantAttachmentItem}
                type="primary"
              />
            </div>
          )}
          {!!subTypes.length ? (
            <Form.Item label="Наименование">
              {getFieldDecorator(SUBNAME_KEY, {
                initialValue: currentSubname,
                rules: [{ required: true, message: 'Выберите наименование' }],
              })(
                <Select
                  allowClear
                  showSearch
                  style={{ width: '100%', maxWidth: '230px' }}
                  placeholder="Выберите или введите наименование"
                  onChange={handleChange}
                  onSearch={handleSearch}
                >
                  {subTypes?.map(({ id, name }) => (
                    <Select.Option
                      disabled={
                        usedSubTypes.has(name) && currentSubname !== name
                      }
                      key={id}
                      value={name}
                    >
                      <Tooltip
                        title={
                          usedSubTypes.has(name) &&
                          currentSubname !== name &&
                          'Документ с этим наименованием уже загружен'
                        }
                      >
                        {name}
                      </Tooltip>
                    </Select.Option>
                  ))}
                </Select>
              )}
            </Form.Item>
          ) : (
            <Form.Item label="Наименование">
              {getFieldDecorator(SUBNAME_KEY, {
                initialValue: currentSubname,
                rules: [{ required: true, message: 'Введите наименование' }],
              })(
                <Input
                  onChange={handleInputChange}
                  placeholder="Введите наименование"
                />
              )}
            </Form.Item>
          )}
        </div>

        <div className={cn(styles.content, styles.content_right)}>
          <Form.Item label="Вложение">
            {attach ? (
              <div className={styles.fileName}>
                <Icon
                  onClick={handleRemoveAttachment}
                  type="delete"
                  style={{ marginRight: '5px' }}
                />
                {attach[0].originFileObj.name}
              </div>
            ) : (
              <Upload
                multiple={false}
                customRequest={handleAddAttachment}
                fileList={attach && !!attach.length ? attach : []}
                beforeUpload={beforeUpload}
                disabled={!currentSubname}
                showUploadList={false}
              >
                <Tooltip title={!currentSubname ? 'Выберите наименование' : ''}>
                  <Button
                    icon="file-add"
                    type="dashed"
                    disabled={!currentSubname}
                  >
                    Перетащите файл или нажмите для выбора
                  </Button>
                </Tooltip>
              </Upload>
            )}
          </Form.Item>
          {index !== 0 && (
            <div className={styles.remove}>
              <Button
                shape="circle"
                icon="delete"
                type="danger"
                onClick={() => handleRemoveField(fieldKey)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
